import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { GET_CURRENT_USER_CALENDAR, LIST_MY_APPOINTMENTS } from '../graphql/appointments';
import { CalendarMonthView } from '../components/calendar/CalendarMonthView';
import { CalendarDayView } from '../components/calendar/CalendarDayView';
import { CalendarWeekView } from '../components/calendar/CalendarWeekView';
import { AppointmentModal } from '../components/calendar/AppointmentModal';
import { GoogleCalendarSettings } from '../components/calendar/GoogleCalendarSettings';

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface Appointment {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  status: string;
  caseId?: string;
  clientId?: string;
}

interface ListMyAppointmentsData {
  listMyAppointments: Appointment[];
}

interface GetCurrentUserCalendarData {
  getCurrentUser: {
    googleCalendarConnected: boolean;
  };
}

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

type ViewMode = 'month' | 'week' | 'day';

export function Calendar() {
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [preFilledDateTime, setPreFilledDateTime] = useState<{ date: Date; hour: number } | null>(null);

  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data: calendarData } = useQuery<GetCurrentUserCalendarData>(GET_CURRENT_USER_CALENDAR);

  // Calculate date range based on view mode
  const getDateRange = () => {
    const start = new Date(selectedDate);
    const end = new Date(selectedDate);

    if (viewMode === 'month') {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    } else if (viewMode === 'week') {
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      end.setDate(start.getDate() + 6);
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  };

  const { startDate, endDate } = getDateRange();

  const { data: appointmentsData, loading, refetch } = useQuery<ListMyAppointmentsData>(LIST_MY_APPOINTMENTS, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
  });

  const user = userData?.getCurrentUser;
  const appointments = appointmentsData?.listMyAppointments || [];
  const googleCalendarConnected = calendarData?.getCurrentUser?.googleCalendarConnected || false;

  const handlePrevious = () => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setSelectedDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const handleCreateAppointment = () => {
    setSelectedAppointment(null);
    setPreFilledDateTime(null);
    setIsAppointmentModalOpen(true);
  };

  const handleCreateAppointmentAtTime = (date: Date, hour: number) => {
    setSelectedAppointment(null);
    setPreFilledDateTime({ date, hour });
    setIsAppointmentModalOpen(true);
  };

  const handleEditAppointment = (appointment: any) => {
    setSelectedAppointment(appointment);
    setPreFilledDateTime(null);
    setIsAppointmentModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAppointmentModalOpen(false);
    setSelectedAppointment(null);
    setPreFilledDateTime(null);
    refetch();
  };

  const getDateRangeText = () => {
    if (viewMode === 'month') {
      return selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const start = new Date(selectedDate);
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  if (loading && appointments.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />

      <main className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-aalaw-charcoal">Calendar</h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 hidden sm:block">Manage your appointments and schedule</p>
              </div>
              <Button
                onClick={handleCreateAppointment}
                size="sm"
                className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>New</span>
              </Button>
            </div>
            
            {/* Mobile action buttons */}
            <div className="flex items-center gap-2 sm:hidden">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="flex items-center gap-1.5 flex-1 text-xs"
                title="Refresh calendar"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSettingsOpen(true)}
                className="flex items-center gap-1.5 flex-1 text-xs"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Settings</span>
              </Button>
            </div>

            {/* Desktop action buttons */}
            <div className="hidden sm:flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="flex items-center gap-2"
                title="Refresh calendar"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSettingsOpen(true)}
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Settings</span>
              </Button>
            </div>
          </div>

          {/* Google Calendar Status */}
          {googleCalendarConnected && (
            <div className="mt-3 sm:mt-4 flex items-center gap-2 text-xs sm:text-sm text-green-600 bg-green-50 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="hidden sm:inline">Google Calendar connected - Appointments sync automatically</span>
              <span className="sm:hidden">Google Calendar synced</span>
            </div>
          )}
        </div>

        {/* Calendar Controls */}
        <Card className="mb-4 sm:mb-6">
          <div className="p-2 sm:p-4">
            <div className="flex flex-col gap-3 sm:gap-4">
              {/* Navigation */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1 sm:gap-2">
                  <Button variant="outline" size="sm" onClick={handleToday} className="text-xs sm:text-sm px-2 sm:px-3">
                    Today
                  </Button>
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    <Button variant="ghost" size="sm" onClick={handlePrevious} className="p-1 sm:p-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleNext} className="p-1 sm:p-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Button>
                  </div>
                </div>
                <h2 className="text-sm sm:text-lg font-semibold text-gray-900 text-right flex-1 truncate">
                  {getDateRangeText()}
                </h2>
              </div>

              {/* View Mode Selector */}
              <div className="flex items-center gap-0.5 sm:gap-1 bg-gray-100 p-0.5 sm:p-1 rounded-lg">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('month')}
                  className={`flex-1 text-xs sm:text-sm ${viewMode === 'month' ? 'bg-white shadow-sm text-[#C41E3A] hover:text-[#C41E3A]' : 'text-gray-700'}`}
                >
                  Month
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('week')}
                  className={`flex-1 text-xs sm:text-sm ${viewMode === 'week' ? 'bg-white shadow-sm text-[#C41E3A] hover:text-[#C41E3A]' : 'text-gray-700'}`}
                >
                  Week
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('day')}
                  className={`flex-1 text-xs sm:text-sm ${viewMode === 'day' ? 'bg-white shadow-sm text-[#C41E3A] hover:text-[#C41E3A]' : 'text-gray-700'}`}
                >
                  Day
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Calendar View */}
        <Card>
          {viewMode === 'month' && (
            <CalendarMonthView
              selectedDate={selectedDate}
              appointments={appointments}
              onAppointmentClick={handleEditAppointment}
              onDateClick={(date) => {
                setSelectedDate(date);
                setViewMode('day');
              }}
            />
          )}
          {viewMode === 'week' && (
            <CalendarWeekView
              selectedDate={selectedDate}
              appointments={appointments}
              onAppointmentClick={handleEditAppointment}
              onTimeSlotClick={handleCreateAppointmentAtTime}
            />
          )}
          {viewMode === 'day' && (
            <CalendarDayView
              selectedDate={selectedDate}
              appointments={appointments}
              onAppointmentClick={handleEditAppointment}
              onTimeSlotClick={handleCreateAppointmentAtTime}
            />
          )}
        </Card>
      </main>

      {/* Appointment Modal */}
      {isAppointmentModalOpen && (
        <AppointmentModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
          preFilledDateTime={preFilledDateTime}
        />
      )}

      {/* Google Calendar Settings Modal */}
      {isSettingsOpen && (
        <GoogleCalendarSettings
          onClose={() => setIsSettingsOpen(false)}
        />
      )}
    </div>
  );
}
